import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type Page } from './api';
import type { AsyncState } from '../ui';

export type FiltroFila = 'ALL' | 'PENDING' | 'OPEN' | 'RESOLVED' | 'MINE';

export interface ConversaResumo {
  id: string;
  status: 'PENDING' | 'OPEN' | 'RESOLVED';
  assignedTo: string | null;
  lastMessageAt: string | null;
  agent: { id: string; name: string } | null;
  contact: { displayName:string|null;profileName:string|null;waId:string;crmContactId:string|null;crmMergedIntoId:string|null;crmJobTitle:string|null;crmAccountId:string|null;crmAccountName:string|null;crmOrganizations?:Array<{id:string;name:string;relationshipRole?:string|null}>;crmSyncedAt:string|null };
  phoneNumber: { displayNumber: string; verifiedName: string | null };
  counts: { messages: number; notes: number };
  lastMessage: { direction: 'INBOUND' | 'OUTBOUND'; status: string; createdAt: string; preview: string } | null;
}

const MIN_MS = 4000;
const MAX_MS = 30000;
const TAKE = 30;

/**
 * Lista da fila com atualização adaptativa e páginas acumuladas.
 *
 * Três decisões que vieram de medição, não de gosto:
 *
 * 1. O filtro vai na query, não em memória. Antes o cliente baixava a fila
 *    inteira e filtrava no navegador — "Aguardando" custava o mesmo que
 *    "Todas", e com mil conversas eram 40 MB por atualização.
 * 2. `If-None-Match`. Uma caixa parada responde 304 sem corpo: trinta segundos
 *    de aba aberta passaram de ~7 requisições com corpo completo para três
 *    respostas vazias.
 * 3. O intervalo cresce quando nada muda (4s → 30s) e volta para 4s assim que
 *    algo muda. Intervalo fixo de 4s mantinha o servidor ocupado com uma aba
 *    esquecida aberta a tarde inteira.
 *
 * A quarta decisão é nova e corrige um defeito: o `nextCursor` era guardado num
 * estado que ninguém lia. A fila parava na primeira página — trinta conversas —
 * e não havia nem indicação de que existiam outras. Quem tinha fila grande via
 * um recorte arbitrário e nenhum aviso.
 */
export function useConversas(filtro: FiltroFila) {
  const [state, setState] = useState<AsyncState<ConversaResumo[]>>({ status: 'loading' });
  const [temMais, setTemMais] = useState(false);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const etag = useRef<string | null>(null);
  const atrasoRef = useRef(MIN_MS);
  const itensRef = useRef<ConversaResumo[]>([]);
  /** Cursor da PRÓXIMA página — vem sempre da última página buscada. */
  const cursorRef = useRef<string | null>(null);
  /** Quantas páginas estão na tela. Governa o que a atualização de fundo pode
   *  sobrescrever: com uma só, ela manda em tudo; com mais, só na primeira. */
  const paginasRef = useRef(1);

  /**
   * Junta a primeira página recém-buscada com o que já estava na tela.
   *
   * A ordenação é keyset por `(lastMessageAt desc, id desc)`: uma conversa que
   * recebe mensagem sobe para a primeira página e precisa sair de onde estava,
   * senão apareceria duas vezes. Deduplicar por id resolve os dois sentidos —
   * a que subiu sai da cauda, e a que foi empurrada para fora da primeira
   * página continua visível na cauda, que é onde ela realmente está agora.
   */
  const mesclar = (primeira: ConversaResumo[], anteriores: ConversaResumo[]): ConversaResumo[] => {
    if (paginasRef.current <= 1) return primeira;
    const naPrimeira = new Set(primeira.map((c) => c.id));
    return [...primeira, ...anteriores.filter((c) => !naPrimeira.has(c.id))];
  };

  const buscar = useCallback(
    async (silencioso: boolean): Promise<boolean> => {
      const query = new URLSearchParams({ filter: filtro, take: String(TAKE) });
      try {
        const resposta = await api<Response>(`/workspace/conversations?${query}`, {
          raw: true,
          headers: etag.current ? { 'if-none-match': etag.current } : {},
        });
        if (resposta.status === 304) return false;
        if (!resposta.ok) throw new ApiError(resposta.status, 'Não foi possível carregar a fila.');

        etag.current = resposta.headers.get('etag');
        const pagina = (await resposta.json()) as Page<ConversaResumo>;
        itensRef.current = mesclar(pagina.items, itensRef.current);
        // Com mais de uma página na tela, o cursor pertence à última buscada:
        // sobrescrevê-lo com o da primeira faria "Carregar mais" trazer de novo
        // a página dois, para sempre.
        if (paginasRef.current <= 1) {
          cursorRef.current = pagina.nextCursor;
          setTemMais(Boolean(pagina.nextCursor));
        }
        setState(itensRef.current.length === 0
          ? { status: 'empty' }
          : { status: 'ready', data: itensRef.current });
        return true;
      } catch (erro) {
        // Falha numa atualização de fundo não apaga o que já está na tela: o
        // atendente continua trabalhando com a lista que tem, e a faixa avisa
        // que ela pode estar velha. Só a primeira carga vira tela de erro.
        const mensagem = erro instanceof ApiError ? erro.message : 'Sem conexão com o servidor.';
        setState(
          silencioso && itensRef.current.length
            ? { status: 'partial', data: itensRef.current, reason: `${mensagem} Mostrando a última lista carregada.` }
            : { status: 'error', message: mensagem, onRetry: () => void buscar(false) },
        );
        return false;
      }
    },
    [filtro],
  );

  /**
   * Próxima página.
   *
   * Falhar aqui NÃO derruba a lista para o estado de erro: o que já está na
   * tela continua servindo. O botão volta a ficar disponível e a pessoa tenta
   * de novo — perder trinta conversas já carregadas porque a trigésima primeira
   * não veio seria uma troca ruim.
   */
  const carregarMais = useCallback(async () => {
    const cursor = cursorRef.current;
    if (!cursor || carregandoMais) return;
    setCarregandoMais(true);
    try {
      const query = new URLSearchParams({ filter: filtro, take: String(TAKE), cursor });
      const pagina = await api<Page<ConversaResumo>>(`/workspace/conversations?${query}`);
      const conhecidos = new Set(itensRef.current.map((c) => c.id));
      itensRef.current = [...itensRef.current, ...pagina.items.filter((c) => !conhecidos.has(c.id))];
      cursorRef.current = pagina.nextCursor;
      paginasRef.current += 1;
      setTemMais(Boolean(pagina.nextCursor));
      setState({ status: 'ready', data: itensRef.current });
    } catch {
      setTemMais(true);
    } finally {
      setCarregandoMais(false);
    }
  }, [filtro, carregandoMais]);

  // Troca de filtro invalida o ETag: a resposta anterior era de outra consulta.
  useEffect(() => {
    etag.current = null;
    itensRef.current = [];
    cursorRef.current = null;
    paginasRef.current = 1;
    atrasoRef.current = MIN_MS;
    setTemMais(false);
    setState({ status: 'loading' });
    void buscar(false);
  }, [buscar]);

  useEffect(() => {
    let vivo = true;
    let timer: number;
    const agendar = () => {
      timer = window.setTimeout(async () => {
        if (!vivo) return;
        // Aba oculta não precisa de atualização; volta a atualizar ao focar.
        if (document.visibilityState === 'visible') {
          const mudou = await buscar(true);
          atrasoRef.current = mudou ? MIN_MS : Math.min(atrasoRef.current * 1.5, MAX_MS);
        }
        if (vivo) agendar();
      }, atrasoRef.current);
    };
    agendar();
    const aoFocar = () => { atrasoRef.current = MIN_MS; };
    document.addEventListener('visibilitychange', aoFocar);
    return () => { vivo = false; window.clearTimeout(timer); document.removeEventListener('visibilitychange', aoFocar); };
  }, [buscar]);

  return { state, temMais, carregandoMais, carregarMais, recarregar: () => void buscar(false) };
}
